import {tailwindConfig} from "../mosaic/utils/Utils.js";

const colors = {
    "#003A7D": "#004C9A",
    "#0077b6": "#0090e3",
    "#0096c7": "#00b0ea",
    "#00b4d8": "#33c9eb",
    "#48cae4": "#6ad7ed",
    "#90e0ef": "#ade9f6",

    "#3c096c": "#56108f",
    "#5a189a": "#7b2cbf",
    "#7b2cbf": "#9d4edd",
    "#9d4edd": "#c77dff",
    "#c77dff": "#ddaaff",
    "#e0aaff": "#f2d6ff",

    "#62040a": "#8b050e",
    "#9d0610": "#b30716",
    "#d60000": "#e00000",
    "#ff141c": "#ff2b32",
    "#ff3d44": "#ff5a5f",
    "#ff666b": "#ff7f85",
    "#ff8a92": "#ffa3a8",

    "#145218": "#1c7a2b",
    "#1f6924": "#268b2d",
    "#509724": "#66b936",
    "#80c423": "#97da34",
    "#8edf34": "#a3f845",
    "#9cf945": "#b2ff57",
    "#aafc57": "#c4ff71",

    "#ff7b00": "#ff9500",
    "#ff9500": "#ffaa00",
    "#ffaa00": "#ffc300",
    "#ffc300": "#ffdd00",
    "#ffdd00": "#ffec17",
    "#ffec17": "#ffff4a",
};

const contrastColors = {
    "#003A7D": "#004C9A",
    "#179eea": "#2dacf3",
    "#6bf6ff": "#8cf3f9",

    "#642cbf": "#7549bc",
    "#b96ef2": "#ac56ed",
    "#f4abf7": "#f28cf6",

    "#a70000": "#d30606",
    "#f73030": "#f55656",
    "#fd9a9a": "#fa8080",

    "#285020": "#367829",
    "#29aa01": "#38d608",
    "#9cff2e": "#6dc60a",

    "#ff7b00": "#ff9500",
    "#ffaa00": "#ffc300",
    "#ffdd00": "#ffec17",
};

const grayscaleColors = {
    "#4a4a4a": "#575757",
    "#505050": "#5d5d5d",
    "#555555": "#626262",
    "#5a5a5a": "#676767",
    "#606060": "#6d6d6d",
    "#656565": "#727272",
    "#6a6a6a": "#777777",
    "#707070": "#7d7d7d",
    "#757575": "#828282",
    "#7a7a7a": "#878787",
    "#808080": "#8d8d8d",
    "#858585": "#929292",
    "#8a8a8a": "#979797",
    "#8f8f8f": "#9c9c9c",
    "#959595": "#a2a2a2",
};

//
// =====================================================================================================================
//

const convertRgbToRgbaWithOpacity = (hex, opacity) => {
    let cleanHex = hex.replace('#', '');

    if (cleanHex.length === 3) {
        cleanHex = cleanHex.split('').map(char => char + char).join('');
    }

    const r = parseInt(cleanHex.slice(0, 2), 16);
    const g = parseInt(cleanHex.slice(2, 4), 16);
    const b = parseInt(cleanHex.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

//
// =====================================================================================================================
//

const safeColorIndex = (index, object) => {
    let length = Object.keys(object).length;
    return ((index % length) + length) % length;
};

// == == == ==

const colorKeyAt = (object, index, opacity) => {
    return convertRgbToRgbaWithOpacity(Object.keys(object)[safeColorIndex(index, object)], opacity);
};

const colorValueAt = (object, index, opacity) => {
    return convertRgbToRgbaWithOpacity(Object.values(object)[safeColorIndex(index, object)], opacity);
};

// == == == ==

export function useGetBackgroundColor() {
    return (index, opacity = 1) => colorKeyAt(colors, index, opacity)
}

export function useGetBackgroundHoverColor() {
    return (index, opacity = 1) => colorValueAt(colors, index, opacity)
}

export function useGetContrastBackgroundColor() {
    return (index, opacity = 1) => colorKeyAt(contrastColors, index, opacity);
}

export function useGetContrastBackgroundHoverColor() {
    return (index, opacity = 1) => colorValueAt(contrastColors, index, opacity);
}

export function useGetGrayscaleBackgroundColor() {
    return (index, opacity = 1) => colorKeyAt(grayscaleColors, index, opacity);
}

export function useGetGrayscaleBackgroundHoverColor() {
    return (index, opacity = 1) => colorValueAt(grayscaleColors, index, opacity);
}

// =====================================================================================================================
// Pie colors
// =====================================================================================================================


export function getPieColor(index, type = 'default') {
    const c = tailwindConfig().theme.colors;
    const pieColors = [
        c.violet?.[500] ?? '#8b5cf6',
        c.green?.[500] ?? '#22c55e',
        c.yellow?.[500] ?? '#eab308',
        c.sky?.[500] ?? '#0ea5e9',
        c.red?.[500] ?? '#ef4444',
        c.orange?.[500] ?? '#f97316',
        c.amber?.[500] ?? '#f59e0b',
        c.lime?.[500] ?? '#84cc16',
        c.emerald?.[500] ?? '#10b981',
        c.teal?.[500] ?? '#14b8a6',
        c.cyan?.[500] ?? '#06b6d4',
        c.blue?.[500] ?? '#3b82f6',
        c.indigo?.[500] ?? '#6366f1',
        c.fuchsia?.[500] ?? '#d946ef',
        c.pink?.[500] ?? '#ec4899',
    ];

    const safeIndex = index % pieColors.length;
    const selectedColor = pieColors[safeIndex];
    return type === 'hover' ? convertRgbToRgbaWithOpacity(selectedColor, .8) : selectedColor;
}

export function getBarColor(index, type = 'default') {
    const c = tailwindConfig().theme.colors;
    const pieColors = [
        c.violet?.[700] ?? '#6d28d9',
        c.violet?.[500] ?? '#8b5cf6',
        c.violet?.[300] ?? '#c4b5fd',
        c.violet?.[200] ?? '#ddd6fe',
        c.sky?.[700] ?? '#0369a1',
        c.sky?.[500] ?? '#0ea5e9',
        c.sky?.[300] ?? '#7dd3fc',
        c.sky?.[100] ?? '#e0f2fe',
    ];

    const safeIndex = index % pieColors.length;
    const selectedColor = pieColors[safeIndex];
    return type === 'hover' ? convertRgbToRgbaWithOpacity(selectedColor, .9) : selectedColor;
}

//
// =====================================================================================================================
//

/**
 * @deprecated
 */
export function getBackgroundColors() {
    return Object.keys(colors);
}

/**
 * @deprecated
 */
export function getBackgroundHoverColors() {
    return Object.values(colors);
}

/**
 * @deprecated
 */
export function getContrastBackgroundColors() {
    return Object.keys(contrastColors);
}

/**
 * @deprecated
 */
export function getContrastBackgroundHoverColors() {
    return Object.values(contrastColors);
}
